'use client';

import { type ComponentProps, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import logMapHeatmapTapped from '@alltrails/amplitude/events/logMapHeatmapTapped';
import logMapLayerButtonTapped from '@alltrails/amplitude/events/logMapLayerButtonTapped';
import logMapViewAdjustmentClicked from '@alltrails/amplitude/events/logMapViewAdjustmentClicked';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Expand from '@alltrails/denali/icons/Expand';
import CompassButton from '@alltrails/maps/components/CompassButton/CompassButton';
import StyleButton from '@alltrails/maps/components/StyleButton/StyleButton';
import TerrainButton from '@alltrails/maps/components/TerrainButton/TerrainButton';
import ZoomButtons from '@alltrails/maps/components/ZoomButtons/ZoomButtons';
import useMap from '@alltrails/maps/hooks/useMap';
import useIsPage from '@/hooks/useIsPage';
import { useAppSelector } from '@/utils/redux';

type MapControlsProps = {
  currentLocationButton?: ReactNode;
} & Pick<ComponentProps<typeof StyleButton>, 'handleFailedEntitlementCheck' | 'pauseFocusTrap'>;

const MapControls = ({ currentLocationButton, handleFailedEntitlementCheck, pauseFocusTrap }: MapControlsProps) => {
  const intl = useIntl();
  const map = useMap();
  const { isCustomRoute, isMembersCustomRoute } = useIsPage();
  const isMobile = useIsMobileSizedScreen();

  const { activeTrails, initialMapBounds, showRecenterButton } = useAppSelector(state => ({
    activeTrails: state.map.activeTrails,
    initialMapBounds: state.explore.initialMapBounds,
    showRecenterButton: state.explore.showRecenterButton
  }));

  const onRecenterClick = useMemo(() => {
    if (showRecenterButton && map && initialMapBounds) {
      const { latitudeBottomRight, longitudeBottomRight, latitudeTopLeft, longitudeTopLeft } = initialMapBounds;
      return () => {
        logMapViewAdjustmentClicked({ action_taken: 'recenter clicked' });
        map.fitBounds([
          [longitudeTopLeft, latitudeBottomRight],
          [longitudeBottomRight, latitudeTopLeft]
        ]);
      };
    }
    return undefined;
  }, [initialMapBounds, map, showRecenterButton]);

  return (
    <>
      <StyleButton
        handleFailedEntitlementCheck={handleFailedEntitlementCheck}
        mapLocation="explore"
        onStyleModalChange={isOpen => {
          if (isOpen) {
            logMapViewAdjustmentClicked({ action_taken: 'map layer button clicked' });
            logMapLayerButtonTapped({ map_location: isCustomRoute || isMembersCustomRoute ? 'custom route' : 'explore' });
          }
        }}
        onMapOverlayTapped={(overlay, isAddition) => {
          if (overlay === 'heatmap') {
            logMapHeatmapTapped({
              is_addition: isAddition,
              map_location: isCustomRoute || isMembersCustomRoute ? 'custom route' : 'explore',
              trail_id: activeTrails?.[0]?.ID
            });
            if (isAddition) {
              window.Sprig?.('identifyAndTrack', {
                eventName: 'Map_Heatmap_Tapped',
                properties: {
                  map_location: 'explore',
                  trail_id: activeTrails?.[0]?.ID,
                  is_addition: true
                }
              });
            }
          }
        }}
        pauseFocusTrap={pauseFocusTrap}
      />
      <TerrainButton onClick={() => logMapViewAdjustmentClicked({ action_taken: 'three d clicked' })} />
      {!isMobile && (
        <ZoomButtons
          onZoomInClick={() => logMapViewAdjustmentClicked({ action_taken: 'zoomed in' })}
          onZoomOutClick={() => logMapViewAdjustmentClicked({ action_taken: 'zoomed out' })}
        />
      )}
      {currentLocationButton}
      {onRecenterClick && (
        <IconButton
          icon={{ Component: Expand }}
          onClick={onRecenterClick}
          title={intl.formatMessage({ defaultMessage: 'Re-center' })}
          variant="elevated"
        />
      )}
      <CompassButton onClick={() => logMapViewAdjustmentClicked({ action_taken: 'compass clicked' })} />
    </>
  );
};

export default MapControls;
